import { useEffect, useRef } from 'react';

type TInsideSelector = string | React.RefObject<HTMLElement | null>;

interface IUseDismissOutsideOptions {
  /** 开关，通常传浮窗 isOpen；false 时所有监听自动 cleanup */
  enabled: boolean;
  /** 关闭回调 */
  onClose: () => void;
  /**
   * 白名单：点击命中以下任一目标内部时不关闭。
   * 支持 className 字符串（不带 .）或 React.RefObject。
   * Portal 场景必须传 className（ref 拿不到 portal 节点）。
   */
  insideSelectors: TInsideSelector[];
}

/**
 * 浮窗外点击 / iframe 切走时统一关闭的公共 hook。三件套：
 * ① document.mousedown capture：同 doc 内点击，绕过中间 stopPropagation
 * ② window.focus()：仅在 main-frame 失焦时抢焦点，确保 blur 可触发且不打断 iframe 内已有焦点
 * ③ window.blur：iframe 内 / 宿主页面点击会让 main-frame 失焦，与同源/跨域无关
 *
 * 不要替换组件自带的 outsideClick（如 Tea Dropdown 内置），本 hook 仅补一层 capture handler，
 * 同 doc 路径下两者幂等（最终都 setOpen(false)）。
 */
export const useDismissOutside = ({
  enabled,
  onClose,
  insideSelectors,
}: IUseDismissOutsideOptions) => {
  const onCloseRef = useRef(onClose);
  const insideSelectorsRef = useRef(insideSelectors);
  useEffect(() => {
    onCloseRef.current = onClose;
    insideSelectorsRef.current = insideSelectors;
  });

  useEffect(() => {
    if (!enabled) return;

    const isInside = (target: EventTarget | null) => {
      if (!(target instanceof Element)) return false;
      return insideSelectorsRef.current.some((sel) => {
        if (typeof sel === 'string') {
          return target.closest(`.${sel}`) !== null;
        }
        return sel.current?.contains(target);
      });
    };

    const onMouseDown = (evt: MouseEvent) => {
      if (isInside(evt.target)) return;
      onCloseRef.current();
    };
    const onBlur = () => onCloseRef.current();

    if (!document.hasFocus()) {
      window.focus();
    }
    document.addEventListener('mousedown', onMouseDown, true);
    window.addEventListener('blur', onBlur);
    return () => {
      document.removeEventListener('mousedown', onMouseDown, true);
      window.removeEventListener('blur', onBlur);
    };
  }, [enabled]);
};

export default useDismissOutside;
